import Contract from "slotworks/utils/contract";
import gameState from "slotworks/model/game-state";
import G288SpinRecord from "integration/spin-record";
import graphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import { Sprite } from "pixi.js";
import * as TweenJS from "slotworks/utils/tween";
import { sequence } from "slotworks/utils/race-utils";
import Components from "slotworks/components/components";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import Timer from "slotworks/utils/timer";
import { ShakeLayers, Shake } from "commands/shake-command";
import G288ReelSpinner from "components/matrix/transitions/reel-spinner";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import deviceInfo from "slotworks/utils/device-info";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import soundManager from "slotworks/sound/sound-manager";
import { isBigWinInBaseGame } from "utils/utils";
import MessagesComponent from "components/messages/messages-component";
import betModel from "slotworks/model/bets/bet-model";
import settingsModel from "slotworks/model/settings/settings-model";
import animationManager from "slotworks/graphics/animation/animation-manager";

const ANTICIPATION_ANIMATION_TIME = 11 * (1000 / 16);
const FRAME_ANIMATION_TIME = 19 * (1000 / 16);

interface IFrameAnimation {
    type: number;
    reel: number; 
    symbol: number;
}

export default class StackAddComponent {
    
    private id: string;
    private symbolSprite: Sprite;
    private symbolSprites: Sprite[];

    private container: PIXI.Container = null;
    private topSymbolsContainer: PIXI.Container = null;

    private animations: AnimatedSprite[] = [];

    private shakeTween: TweenJS.Tween = null;
    private shakeTopSymbolTween: TweenJS.Tween = null;
    private shakeTopTween: TweenJS.Tween = null;

    private freespinsTriggered: boolean;

    private timers: number[] = [];
    private tweens: TweenJS.Tween[] = [];
    private anticipationStartTime: number = 0;

    private orientationString: string;
    private orientationChanged: boolean;

    private portrait: boolean;
    private preWin: boolean;
    private skipped: boolean;

    private skipAvailable: boolean = false;

    private anticipatingSymbols: SymbolComponent[] = [];
    private done: boolean = false;

    public start(spinners: G288ReelSpinner[]): Contract<void> {
        return new Contract(resolve => {
            this.setSymbol();
            this.skipped = false;
            this.done = false;

            Layers.Custom.get("StackAddForeground").setScene("stackadd").execute();
            this.anticipatingSymbols = [];
            this.init();
            this.orientationChanged = false;
            this.skipAvailable = true;

            const funcs: (() => Contract<void>)[] = [];
            funcs.push(() => Contract.getTimeoutContract(100));
            for(let i = 2; i <= 5; i++) {
                Layers.Custom.get("StackAddForeground").getSprite(`reel${i}_add_bottom`).visible = false;
                funcs.push(() => this.moveSymbolToPosition(i));
                funcs.push(() => this.addSymbolToReelAnimation(i, spinners[i - 1]));
            }
            this.startFirstReelSymbolAnticipation();
            this.backgroundZoomIn();  
            sequence(funcs).then(() => {
                Layers.Custom.get("StackAddForeground").remove(this.symbolSprite);
                this.symbolSprite.destroy({ children: true });
                this.symbolSprite = null;
                resolve(null); 
            });
        })
    }

    public startDelayedAnticipations(symbols: SymbolComponent[], delay: number) {
        if(this.skipped) return;
        this.timers.push(Timer.setTimeout(() => {
            for(const symbol of symbols) {
                symbol.anticipate().execute();
                this.anticipatingSymbols.push(symbol);
            }
        }, delay));
    }

    public stopAnticipatingSymbols() {
        for(const symbol of this.anticipatingSymbols) {
            symbol.static();
        }
        this.anticipatingSymbols.length = 0;
    }

    public startDelayedFrames(frames: IFrameAnimation[], delay: number) {
        if(this.skipped) return;
        this.timers.push(Timer.setTimeout(() => {
            for(const frame of frames) {
                this.addFrameAnticipation(frame.type, frame.reel, frame.symbol);
            }
        }, delay));
    }

    public afterReelStop(index: number): Contract<void> {
        return new Contract(resolve => {
            soundManager.customEvent("stack_land_reel_" + (index + 1));    
            // this.skipAvailable = this.checkNoWinForReel(index);       
            const symbols = Components.matrix.getReels()[index].getSymbols();
            let start = 0;
            const anticipatingSymbols: SymbolComponent[] = [];
            const frames: IFrameAnimation[] = [];
            for(let i = 0; i < symbols.length; i++) {
                const symbol = symbols[i];
                if(symbol.symbolId == this.id || symbol.symbolId == "WILD") {
                    anticipatingSymbols.push(symbol);
                } else {
                    if(symbol.symbolId != "SCATTER") {
                        symbol.setSymbolFade(false);
                    }
                    if(i > start) {
                        frames.push({type: i - start, reel: index + 1, symbol: start + 1});
                    }
                    start = i + 1;
                }
            }
            if(start < symbols.length) {
                frames.push({type: symbols.length - start, reel: index + 1, symbol: start + 1});
            }
            const time = Date.now() - this.anticipationStartTime;
            if(anticipatingSymbols.length > 0) {
                this.startDelayedAnticipations(anticipatingSymbols, (ANTICIPATION_ANIMATION_TIME - time % ANTICIPATION_ANIMATION_TIME));
            }
            if(frames.length > 0) {
                this.startDelayedFrames(frames, FRAME_ANIMATION_TIME - time % FRAME_ANIMATION_TIME)
            }
            if(index === 4) {
                soundManager.customEvent("stack_anticipation_stop");
                const record = gameState.getCurrentGame().getCurrentRecord() as G288SpinRecord;
                if(!gameState.getCurrentGame().inFreespinsGame && 
                            Components.bigWin.isBigWin(record.creditsWon, betModel.getTotalBet())) {
                    this.preWin = true;
                    soundManager.customEvent("pre_win");
                }
                this.backgroundZoomOut();
                if(!this.done) resolve(null);
            } else {
                this.timers.push(Timer.setTimeout(() => {
                    if(!this.done) resolve(null)
                }, 1200));
            }
        })
    }

    public skip() {
        this.skipped = true;
        // this.stopAnticipatingSymbols();
        // this.stopAnimatingFrames();
    }

    private stopAnimatingFrames() {
        for(let anim of this.animations) {
            anim.stop();
            anim.parent.removeChild(anim);
            anim.destroy();
        }
        this.animations.length = 0;
    }

    public isSkipAvailable() {
        return this.skipAvailable;
    }

    public skipEnd() {
        this.skipped = true;
        if(!this.id) {
            this.setSymbol();
        }
        for(let i = 1; i < 5; i++) {
            this.afterReelSkip(i);
        }
        this.removeBackgroundTransitions();
        soundManager.customEvent("stack_anticipation_stop");
        if(!this.preWin && isBigWinInBaseGame())  {
            this.preWin = true;
            soundManager.customEvent("pre_win");
        }
        this.stopAnticipatingSymbols();
        this.complete();
    }

    public end() {
        this.complete();
    }

    private checkNoWinForReel(reel: number) {
        if(reel <= 2) return false;
        const record = gameState.getCurrentGame().getCurrentRecord() as G288SpinRecord;
        for(const sym of record.grid[reel]) {
            if(sym == record.stackAddFeatureSymbol) {
                return false;
            }
        }
        return true;
    }

    private updateSmartButton() {
        if(!settingsModel.isSkipDisabled()) {

        }
    }

    private setSymbol() {
        const record = gameState.getCurrentGame().getCurrentRecord() as G288SpinRecord;
        this.freespinsTriggered = record.freespinsTriggered || record.freespinsWon > 0;

        const symbol = record.grid[0][1];
        this.id = symbol;
        this.preWin = false;
    }

    private init() {
        this.symbolSprite = graphicsManager.createSprite(this.id);
        const startPosition = Layers.MatrixContent.localToLocal(Layers.Custom.get("StackAddForeground"), Layers.MatrixContent.getPosition("symbol_1_1"));
        this.symbolSprite.width = startPosition.width;
        this.symbolSprite.height = startPosition.height;
        this.symbolSprite.anchor.set(0.5, 0.5);
        this.symbolSprite.position.set(startPosition.x + startPosition.width * 0.5, startPosition.y + startPosition.height * 0.5);        
        const border = graphicsManager.createSprite("addframe");
        border.anchor.set(0.5, 0.5);
        this.symbolSprite.addChild(border);
        this.symbolSprites = [];
        this.topSymbolsContainer = new PIXI.Container();
        Layers.Custom.get("StackAddForeground").add(this.topSymbolsContainer);
        for(let i = 0; i <= 3; i++) {
            const sprite = graphicsManager.createSprite(this.id);
            sprite.anchor.set(0.5, 0.5);
            const border = graphicsManager.createSprite("addframe");
            border.anchor.set(0.5, 0.5);
            sprite.addChild(border);
            this.topSymbolsContainer.addChild(sprite);
            sprite.visible = false;
            this.symbolSprites.unshift(sprite);
        }
        this.container = new PIXI.Container();
        Layers.Custom.get("StackAddForeground").add(this.container);
        this.container.addChild(this.symbolSprite);
        this.shakeTopSymbolTween = Shake(this.container, 80, 6, 2, 50);
    }

    private afterReelSkip(index: number) {
        soundManager.customEvent("stack_land_reel_" + (index + 1));            
        const symbols = Components.matrix.getReels()[index].getSymbols();
        for(let i = 0; i < symbols.length; i++) {
            const symbol = symbols[i];
            if(symbol.symbolId != this.id && symbol.symbolId != "WILD" && (!this.freespinsTriggered || symbol.symbolId != "SCATTER")) {
                symbol.setSymbolFade(false);
            }
        }
    }

    private moveSymbolToPosition(id: number) {
        const positionId = `reel${id}_${this.id}`;
        const position = Layers.Custom.get("StackAddForeground").getPosition(positionId);
        if(!position || this.skipped) 
            return Contract.empty();
        // if(this.skipped)
        //     return new Contract(resolve => null);
        return new Contract<void>(resolve => {
            this.tweens.push(new TweenJS.Tween(this.symbolSprite.scale)
            .to({x: 1.8, y: 1.8}, 180)
            .yoyo(true)
            .repeat(1)
            .start());
            if(id == 2) {
                this.tweens.push(new TweenJS.Tween(this.symbolSprite)
                .to({x: position.x + position.width* 0.5, y: [position.y + position.height/2 - 60, position.y + position.height/2]}, 420)
                .delay(20)
                .interpolation(TweenJS.Interpolation.Bezier)
                .onComplete(resolve)
                .start());
            } else {
                this.tweens.push(new TweenJS.Tween(this.symbolSprite)
                .to({x: position.x + position.width* 0.5, y: position.y + position.height/2}, 390)
                .onComplete(resolve)
                .start());
            }

            this.cleanTween(this.shakeTween);
            if(id == 2) 
                this.shakeTween = ShakeLayers([Layers.MatrixBackground, Layers.MatrixForeground, Layers.SymbolAnimations, Layers.Custom.get("StackAddForeground"),
                        Layers.Footer, Layers.Custom.get("Animations")], 50, 10, 5, 40);
            else
                this.shakeTween = ShakeLayers([Layers.MatrixBackground, Layers.MatrixForeground, Layers.SymbolAnimations, Layers.Custom.get("StackAddForeground"),
                        Layers.Footer, Layers.Custom.get("Animations")], 50, 6, 4, 40);
            this.cleanTween(this.shakeTopSymbolTween);
            this.shakeTopSymbolTween = Shake(this.container, 60, Math.random() * 8 + 13, 2 + Math.random() * 6, 60);
            this.cleanTween(this.shakeTopTween);
            this.shakeTopTween = Shake(this.topSymbolsContainer, 50, Math.random() * 8 + 4, Math.random() * 8, 50);
        });
    }

    private streaks: AnimatedSprite[] = [];
    private startStreaks(start: number, end: number) {
        for(let i = start; i <= end && i < this.streaks.length; i++) {
            this.streaks[i].visible = true;
            this.streaks[i].loop = true;
            this.streaks[i].gotoAndPlay(0);
        }
    }
    private stopStreaks() {
        for(let i = 0; i < this.streaks.length; i++) {
            this.streaks[i].stop();
            this.streaks[i].visible = false;
        }
    }
    private addSymbolToReelAnimation(reel: number, spinner: G288ReelSpinner): Contract<void> {
        if(this.skipped) return Contract.empty();
        const streakaPosition = Layers.Custom.get("StackAddForeground").getPosition(`streak${reel}a`);
        const streakbPosition = Layers.Custom.get("StackAddForeground").getPosition(`streak${reel}b`);
        const bottom = Layers.Custom.get("StackAddForeground").getSprite(`reel${reel}_add_bottom`);
        if(!streakaPosition || !streakbPosition || !bottom) return Contract.empty();
        if(reel == 2) {
            for(let i = 0; i < 4; i++) {
                this.streaks.push(graphicsManager.createAnimation("add_streak"));
                Layers.Custom.get("StackAddForeground").add(this.streaks[i], false, false);
                if(i > 1) {
                    this.streaks[i].stop();
                    this.streaks[i].visible = false;
                }
                this.streaks[i].loop = true;
            }
            Layers.Custom.get("StackAddForeground").container.removeChild(this.symbolSprites[1]);
            Layers.Custom.get("StackAddForeground").container.addChild(this.symbolSprites[1]);
            Layers.Custom.get("StackAddForeground").container.removeChild(this.symbolSprites[0]);
            Layers.Custom.get("StackAddForeground").container.addChild(this.symbolSprites[0]);            
            Layers.Custom.get("StackAddForeground").container.removeChild(this.symbolSprite);
            Layers.Custom.get("StackAddForeground").container.addChild(this.symbolSprite);
        }
        for(let i = 0; i < 4; i++) {
            const streak = i % 2 ? streakaPosition : streakbPosition;
            this.streaks[i].position.set(streak.x, streak.y);
            this.streaks[i].width = streak.width;
            this.streaks[i].height = streak.height;
        }
        this.startStreaks(0, 1);
        bottom.visible = true;
        soundManager.customEvent("stack_symbols_stacking");
        this.timers.push(Timer.setTimeout(() => this.startStreaks(2, 3), 310));
        this.timers.push(Timer.setTimeout(() => {
            if(!this.skipped) spinner.spliceInSymbol(this.id);
        }, 600));
        return sequence([
            () => this.underAnimation(reel),
            () => Contract.wrap(() => {
                this.stopStreaks();
                bottom.visible = false;
            })
        ])
    }

    private stackAddSymbolRepeats: number = 6;
    private stackAddSymolDownMotion: number = 140;
    private underAnimation(reel: number) {
        return new Contract(resolve => {
            this.symbolSprites[0].visible = true;
            this.symbolSprites[0].position.set(this.symbolSprite.x, this.symbolSprite.y);
            this.symbolSprites[0].scale.set(0.9, 0.9)
            this.tweens.push(new TweenJS.Tween(this.symbolSprites[0])
            .to({y: this.symbolSprite.y + this.symbolSprite.height * 0.8}, this.stackAddSymolDownMotion)
            .repeat(this.stackAddSymbolRepeats)
            .start());
            this.tweens.push(new TweenJS.Tween(this.symbolSprites[0].scale)
            .to({x: 1.1, y: 1.1}, this.stackAddSymolDownMotion)
            .repeat(this.stackAddSymbolRepeats)
            .onComplete(() => this.symbolSprites[0].visible = false)
            .start());
            this.timers.push(Timer.setTimeout(() => {
                this.symbolSprites[1].visible = true;
                this.symbolSprites[1].position.set(this.symbolSprite.x, this.symbolSprite.y + this.symbolSprite.height * 0.8);
                this.symbolSprites[1].scale.set(1, 1)
                this.tweens.push(new TweenJS.Tween(this.symbolSprites[1])
                .to({y: this.symbolSprite.y + this.symbolSprite.height * 1.5}, this.stackAddSymolDownMotion)
                .repeat(this.stackAddSymbolRepeats)
                .onComplete(() => {
                    this.symbolSprites[1].visible = false;
                    resolve(null);
                })
                .start());
            }, this.stackAddSymolDownMotion));
            this.timers.push(Timer.setTimeout(() => {
                this.symbolSprites[2].visible = true;
                this.symbolSprites[2].position.set(this.symbolSprite.x, this.symbolSprite.y + this.symbolSprite.height * 1.5);
                this.symbolSprites[2].scale.set(0.88, 0.88)
                const multiplier = reel <= 3 ? 2.5 : 3.5;
                this.tweens.push(new TweenJS.Tween(this.symbolSprites[2])
                .to({y: this.symbolSprite.y + this.symbolSprite.height * multiplier}, this.stackAddSymolDownMotion)
                .repeat(this.stackAddSymbolRepeats + 1)
                .onComplete(() => {
                    this.symbolSprites[2].visible = false;
                })
                .start());
            }, this.stackAddSymolDownMotion*2));
        });
    }

    private startFirstReelSymbolAnticipation() {
        this.anticipationStartTime = Date.now();
        const reel = Components.matrix.getReels()[0];
        reel.getSymbols().forEach((symbol) => {
            symbol.anticipate().execute();
            this.anticipatingSymbols.push(symbol);
        });
        this.addFrameAnticipation(2, 1, 1);
        soundManager.customEvent("stack_land_reel_1");
        soundManager.customEvent("stack_anticipation");
        if(gameState.getCurrentGame().inFreespinsGame) 
            soundManager.customEvent("freespins_music_silent");
        else
            soundManager.customEvent("base_music_silent");
    }

    private addFrameAnticipation(type: number, reel: number, symbol: number) {
        const anim = graphicsManager.createAnimation(`anticframe${type}`);
        const position = Layers.Custom.get("StackAddForeground").getPosition(`anticframe${type}_${reel}_${symbol}`);
        anim.position.set(position.x, position.y);
        anim.width = position.width;
        anim.height = position.height;
        Layers.Custom.get("StackAddForeground").add(anim, false, true);
        anim.loop = true;
        anim.play();
        this.animations.push(anim);
    }

    private removeBackgroundTransitions = () => {
        this.orientationChanged = true;
        const element = document.querySelector(`${this.orientationString} #Background #default`) as HTMLElement;
        const fselement = document.querySelector(`${this.orientationString} #Background #freespins`) as HTMLElement;

        if(element) {
            element.style.removeProperty("transition");
            if(this.portrait) 
                element.style.transform = "scale(1, 1)";
            else
                element.style.backgroundSize = "auto 100%";   
        }
        
        if(fselement) {
            fselement.style.removeProperty("transition");
            if(this.portrait)
                fselement.style.transform = "scale(1, 1)";
            else 
                fselement.style.backgroundSize = "auto 100%";
        }
    }

    private backgroundZoomIn() {
        this.portrait = false;
        if(!deviceInfo.isDesktop) {
            if(canvasManager.orientation == Orientation.PORTRAIT) {
                this.orientationString =  "#portrait";
                this.portrait = true;
            } else {
                this.orientationString =  "#landscape";                
            }
            canvasManager.onOrientationChange.add(this.removeBackgroundTransitions);
        } else {
            this.orientationString = "#landscape";
        }
        const element = document.querySelector(`${this.orientationString} #Background #default`) as HTMLElement;
        const fselement = document.querySelector(`${this.orientationString} #Background #freespins`) as HTMLElement;
        if(this.portrait) {
            if(gameState.getCurrentGame().inFreespinsGame) {
                if(fselement) {
                    fselement.style.transition = "transform 15s ease-out";
                    fselement.style.transform = "scale(1.3, 1.3)";
                }
            } else {
                if(element) {
                    element.style.transition = "transform 15s ease-out";
                    element.style.transform = "scale(1.3, 1.3)";
                }
            }
        } else {
            if(gameState.getCurrentGame().inFreespinsGame) {
                if(fselement) {
                    fselement.style.transition = "background 15s ease-out";
                    fselement.style.backgroundSize = "auto 130%";
                }
            } else {
                if(element) {
                    element.style.transition = "background 15s ease-out";
                    element.style.backgroundSize = "auto 130%";   
                }
            }
        }
    }

    private backgroundZoomOut() {
        if(this.orientationChanged)
            return;

        const element = document.querySelector(`${this.orientationString} #Background #default`) as HTMLElement;
        const fselement = document.querySelector(`${this.orientationString} #Background #freespins`) as HTMLElement;

        if(this.portrait) {
            if(gameState.getCurrentGame().inFreespinsGame) {
                if(fselement) {
                    fselement.style.transition = "transform 0.6s ease-out";
                    fselement.style.transform = "scale(1, 1)";
                }
            } else {
                if(element) {
                    element.style.transition = "transform 0.6s ease-out";
                    element.style.transform = "scale(1, 1)";
                }
            }
        } else {
            if(gameState.getCurrentGame().inFreespinsGame) {
                if(fselement) {
                    fselement.style.transition = "background 0.8s ease-out";
                    fselement.style.backgroundSize = "auto 100%";
                }
            } else {
                if(element) {
                    element.style.transition = "background 0.8s ease-out";
                    element.style.backgroundSize = "auto 100%";
                }
            }
        }
    }

    private complete() {
        this.done = true;
        if(!deviceInfo.isDesktop) {
            canvasManager.onOrientationChange.remove(this.removeBackgroundTransitions);
        }
        this.anticipatingSymbols.length = 0;
        if(this.streaks) {
            for(let streak of this.streaks) {
                streak.stop();
                streak.visible = false;
                Layers.Custom.get("StackAddForeground").remove(streak);
                streak.destroy();
            }
            this.streaks.length = 0;
        }
        setTimeout(() => this.removeBackgroundTransitions(), 500);
        this.cleanTimers();
        this.cleanTweens();
        this.stopAnimatingFrames();
        if(this.symbolSprites) {
            for(let sprite of this.symbolSprites) {
                sprite.parent.removeChild(sprite);            
                sprite.destroy({ children: true });
            }
            this.symbolSprites.length = 0;
        }        
        if(!this.id) return;
        Layers.Custom.get("StackAddForeground").defaultScene().execute();
        const record = gameState.getCurrentGame().getCurrentRecord() as G288SpinRecord;
        const symbols = Components.matrix.getGridSymbols();        
        for(const symbol of symbols) {
            if(this.id == symbol.symbolId || symbol.symbolId == "WILD") {
                symbol.setSymbolFade(true);
            } else if(symbol.symbolId == "SCATTER") {
                symbol.unlockLayer();
                if(!this.skipped) {
                    symbol.static();
                }
                symbol.setSymbolFade(false);
            } else {
                symbol.setSymbolFade(false);
            }
        }
        this.id = null;
        this.skipAvailable = false;
    }

    private cleanTweens() {
        for(let tween of this.tweens) {
            tween.stop();
            TweenJS.remove(tween);
        }
        this.tweens.length = 0;
        this.cleanTween(this.shakeTween);
        this.shakeTween = null;
        this.cleanTween(this.shakeTopSymbolTween);
        this.shakeTopSymbolTween = null;
        this.cleanTween(this.shakeTopTween);
        this.shakeTopTween = null;
    }

    private cleanTimers() {
        for(let timer of this.timers) {
            Timer.clearTimeout(timer);
        }
        this.timers.length = 0;
    }

    private cleanTween(tween: TweenJS.Tween) {
        if(tween) { 
            tween.stop();
            TweenJS.remove(tween);
        }
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/components/stack-feature/stack-add-component.ts